import { defineStore } from 'pinia';
import http from '@/services/http';
import { useAuthStore } from './auth';
import axios from 'axios';
import eventBus, { EventTypes } from '@/services/eventBus';
import { API_PATHS } from '@/config/api';

// Listener für Auth-Events einrichten
eventBus.addEventListener(EventTypes.AUTH_IMPERSONATED, async (event) => {
  console.log('Auth-Event empfangen, lade Firmendaten neu:', event.detail);
  
  // Company-Store abrufen - außerhalb des Store-Definitionsblocks,
  // um zirkuläre Abhängigkeiten zu vermeiden
  const companyStore = useCompanyStore();
  
  if (companyStore) {
    try {
      // Cache leeren und Daten neu laden
      companyStore.lastFetch = null;
      await companyStore.fetchOwnCompany();
      console.log('Firmendaten nach Impersonierung erfolgreich aktualisiert');
    } catch (err) {
      console.error('Fehler beim Aktualisieren der Firmendaten nach Impersonierung:', err);
    }
  }
});

export const useCompanyStore = defineStore('company', {
  state: () => ({
    currentCompanyId: localStorage.getItem('companyId') || null,
    companies: [], // Cache für geladene Unternehmen
    currentCompany: JSON.parse(localStorage.getItem('currentCompany') || 'null'),
    loading: false,
    error: null,
    dashboardData: null,
    dashboardLoading: false,
    dashboardError: null,
    companyData: null,
    isLoading: false,
    lastFetch: null,
    cacheTimeout: 5 * 60 * 1000, // 5 Minuten Cache
  }),
  
  getters: {
    getCurrentCompany: (state) => state.currentCompany,
    getAllCompanies: (state) => state.companies,
    isLoadingState: (state) => state.loading,
    isDataStale: (state) => {
      if (!state.lastFetch) return true
      return Date.now() - state.lastFetch > state.cacheTimeout
    }
  },
  
  actions: {
    setCurrentCompanyId(id) {
      this.currentCompanyId = id;
      localStorage.setItem('companyId', id);
    },
    
    setCurrentCompany(company) {
      this.currentCompany = company;
      localStorage.setItem('currentCompany', JSON.stringify(company));
      
      if (company && company.primary_color) {
        document.documentElement.style.setProperty('--primary-color', company.primary_color);
      }
    },
    
    addCompany(company) {
      const index = this.companies.findIndex(c => c.id === company.id);
      if (index !== -1) {
        this.companies.splice(index, 1, company);
      } else {
        this.companies.push(company);
      }
      
      if (this.currentCompanyId === company.id) {
        this.currentCompany = company;
        if (company.primary_color) {
          document.documentElement.style.setProperty('--primary-color', company.primary_color);
        }
      }
    },
    
    updateCompany({ id, data }) {
      const company = this.companies.find(c => c.id === id);
      if (company) {
        Object.assign(company, data);
        
        if (this.currentCompanyId === id) {
          this.currentCompany = { ...company };
          if (data.primary_color) {
            document.documentElement.style.setProperty('--primary-color', data.primary_color);
          }
        }
      }
    },
    
    deleteCompany(companyId) {
      this.companies = this.companies.filter(c => c.id !== companyId);
      if (this.currentCompany && this.currentCompany.id === companyId) {
        this.currentCompany = null;
      }
    },
    
    async fetchOwnCompany() {
      // Bei Impersonierung immer neu laden
      const authStore = useAuthStore();
      const isImpersonating = authStore.isImpersonating;
      
      // Wenn Daten im Cache sind und nicht abgelaufen, verwende diese - außer bei Impersonierung
      if (this.currentCompany && !this.isDataStale && !isImpersonating) {
        return this.currentCompany;
      }
      
      this.loading = true;
      this.error = null;
      
      try {
        let response;
        
        // Wenn der Benutzer impersoniert wird und eine company_id hat, verwenden wir direkt diese ID
        if (isImpersonating && authStore.user && authStore.user.company_id) {
          console.log('Impersonierung: Lade Firma direkt mit ID:', authStore.user.company_id);
          response = await http.get(`${API_PATHS.COMPANY}/${authStore.user.company_id}`);
        } else {
          // Normaler Weg für nicht-impersonierte Benutzer
          response = await http.get(API_PATHS.COMPANY_CURRENT);
        }
        
        if (response.data) {
          this.addCompany(response.data);
          this.setCurrentCompany(response.data);
          this.setCurrentCompanyId(response.data.id);
          
          localStorage.setItem('currentCompany', JSON.stringify(response.data));
          localStorage.setItem('companyId', response.data.id);
          
          // Cache-Datum setzen
          this.lastFetch = Date.now();
          
          return response.data;
        } else {
          throw new Error('Keine Firmendaten in der Antwort');
        }
      } catch (error) {
        console.error('FEHLER: fetchOwnCompany -', error.response ? error.response.data.message : error.message);
        
        // Bei 404 oder anderen Fehlern versuchen wir die Daten aus dem localStorage zu verwenden
        const cachedCompany = localStorage.getItem('currentCompany');
        if (cachedCompany) {
          const parsedCompany = JSON.parse(cachedCompany);
          this.currentCompany = parsedCompany;
          return parsedCompany;
        }
        
        this.error = error.response ? error.response.data.message : error.message;
        throw error;
      } finally {
        this.loading = false;
      }
    },
    
    async fetchCompany(id) {
      // Wenn Daten im Cache sind und nicht abgelaufen, verwende diese
      if (this.currentCompany && this.currentCompany.id === id && !this.isDataStale) {
        return this.currentCompany;
      }
      
      // Überprüfen, ob das Unternehmen bereits im Cache ist
      const cachedCompany = this.companies.find(c => c.id === id);
      if (cachedCompany) {
        return cachedCompany;
      }
      
      this.loading = true;
      this.error = null;
      
      try {
        const response = await http.get(`/api/companies/${id}`);
        this.addCompany(response.data);
        
        // Cache-Datum setzen, wenn es sich um das aktuelle Unternehmen handelt
        if (this.currentCompany && this.currentCompany.id === id) {
          this.lastFetch = Date.now();
        }
        
        return response.data;
      } catch (error) {
        console.error('FEHLER: fetchCompany -', error.response ? error.response.data.message : error.message);
        this.error = error.response ? error.response.data.message : error.message;
        throw error;
      } finally {
        this.loading = false;
      }
    },
    
    async updateCompanyData(id, companyData) {
      // Wichtig: Leere oder null-Werte entfernen, da sonst Felder gelöscht werden könnten
      const cleanData = { ...companyData };
      Object.keys(cleanData).forEach(key => {
        // Entferne alle null-Werte außer logo (da null ein gültiger Wert sein kann, um das Logo zu löschen)
        if (cleanData[key] === null && key !== 'logo') {
          delete cleanData[key];
        }
        // Entferne Felder, die nicht aktualisiert werden sollen
        if (['id', 'created_at', 'updated_at'].includes(key)) {
          delete cleanData[key];
        }
      });

      // Debug-Ausgabe
      console.log('Aktualisiere Unternehmensdaten:', cleanData);
      if (cleanData.logo) {
        console.log('Logo enthalten mit Länge:', cleanData.logo.length);
        // Zeige nur die ersten 100 Zeichen des Logos für Debugging
        console.log('Logo-Vorschau:', cleanData.logo.substring(0, 100) + '...');
      }

      try {
        // Verwende die korrekte API-Route für die Aktualisierung der Firmendaten
        const response = await http.put('/api/company/settings', cleanData);
        console.log('Unternehmen erfolgreich aktualisiert:', response.data);
        
        // Aktualisiere das Unternehmen im Store
        this.addCompany(response.data);
        
        // Wenn das aktuelle Unternehmen aktualisiert wurde, setzen wir es auch als aktuelles
        if (this.currentCompany && this.currentCompany.id === id) {
          this.setCurrentCompany(response.data);
        }
        
        return response.data;
      } catch (error) {
        console.error('Fehler beim Aktualisieren des Unternehmens:', error);
        throw error;
      }
    },
    
    async setCurrentCompanyById(id) {
      this.setCurrentCompanyId(id);
      
      try {
        const authStore = useAuthStore();
        const isSuperAdmin = authStore.user && authStore.user.role === 'super_admin';
        
        let company;
        
        if (isSuperAdmin) {
          company = await this.fetchCompany(id);
        } else {
          console.warn('setCurrentCompany wurde für einen normalen Benutzer aufgerufen - verwende fetchOwnCompany stattdessen');
          return null;
        }
        
        this.setCurrentCompany(company);
        return company;
      } catch (error) {
        console.error('Fehler beim Setzen der aktuellen Firma:', error);
        throw error;
      }
    },
    
    async initializeCompany() {
      console.log('Initialisiere Company-Store...');
      
      if (this.currentCompany && this.currentCompany.id) {
        console.log('Verwende bereits geladene Firma:', this.currentCompany.name || 'Unbekannt');
        return this.currentCompany;
      }
      
      const authStore = useAuthStore();
      const currentUser = authStore.user;
      const isSuperAdmin = currentUser && currentUser.role === 'super_admin';
      const isImpersonating = authStore.isImpersonating;
      
      const storedCompanyId = localStorage.getItem('companyId');
      const storedCompany = localStorage.getItem('currentCompany');
      
      try {
        if (storedCompany) {
          try {
            const parsedCompany = JSON.parse(storedCompany);
            if (parsedCompany && parsedCompany.id) {
              console.log('Lade Firma aus localStorage:', parsedCompany.name || 'Unbekannt');
              this.setCurrentCompany(parsedCompany);
              this.setCurrentCompanyId(parsedCompany.id);
              return parsedCompany;
            }
          } catch (e) {
            console.error('Fehler beim Parsen der gespeicherten Firma:', e);
            localStorage.removeItem('currentCompany');
          }
        }
        
        if (currentUser && currentUser.company_id) {
          console.log('Lade Firma des eingeloggten Benutzers:', currentUser.company_id);
          
          try {
            // Bei Impersonierung immer fetchOwnCompany verwenden
            if (isImpersonating) {
              console.log('Impersonierung aktiv: Verwende fetchOwnCompany mit ID', currentUser.company_id);
              return await this.fetchOwnCompany();
            } else if (isSuperAdmin) {
              return await this.fetchCompany(currentUser.company_id);
            } else {
              return await this.fetchOwnCompany();
            }
          } catch (error) {
            console.error('Fehler beim Laden der Firma:', error);
            return null;
          }
        }
        
        return null;
      } catch (error) {
        console.error('Fehler bei der Initialisierung der Firma:', error);
        return null;
      }
    },
    
    async updateCompanySettings({ id, data }) {
      try {
        const response = await http.put(`/api/companies/${id}/settings`, data);
        this.updateCompany({ id, data: response.data });
        return response.data;
      } catch (error) {
        console.error('Fehler beim Aktualisieren der Firmeneinstellungen:', error);
        throw error;
      }
    },
    
    async updateCompanyLogo(formData) {
      this.loading = true;
      this.error = null;
      
      try {
        const id = this.currentCompany?.id;
        if (!id) {
          throw new Error('Keine aktuelle Firma ausgewählt');
        }
        
        // Debug-Ausgabe für das formData-Objekt
        console.log('updateCompanyLogo aufgerufen mit:', formData);
        
        // Extrahiere das Logo aus dem formData-Objekt
        let logoFile = null;
        
        if (formData instanceof FormData) {
          logoFile = formData.get('logo');
          
          // Debug: Prüfe, ob wir das Logo erhalten haben
          if (logoFile) {
            console.log('Logo gefunden in FormData:', logoFile.name, logoFile.type, logoFile.size + ' Bytes');
          } else {
            console.warn('Kein Logo in FormData gefunden!');
            
            // Versuche, alle Schlüssel zu finden
            console.log('FormData Schlüssel:');
            for (const pair of formData.entries()) {
              console.log(pair[0], pair[1]);
            }
          }
        } else if (formData && typeof formData === 'object') {
          logoFile = formData.logo;
        }
        
        if (!logoFile) {
          throw new Error('Kein Logo gefunden. Bitte wählen Sie eine Datei aus.');
        }

        // Erstelle ein neues FormData-Objekt mit ALLEN Firmendaten
        const completeFormData = new FormData();
        
        // Füge das Logo hinzu
        completeFormData.append('logo', logoFile);
        
        // Füge alle vorhandenen Firmendaten hinzu
        if (this.currentCompany) {
          // Wichtige Felder einzeln hinzufügen
          const companyFields = ['name', 'email', 'primary_color', 'phone', 'website', 'street', 'postal_code', 'city'];
          companyFields.forEach(field => {
            if (this.currentCompany[field]) {
              completeFormData.append(field, this.currentCompany[field]);
            }
          });
          
          // ID explizit hinzufügen
          completeFormData.append('id', this.currentCompany.id);
        }
        
        // Debug-Ausgabe vom vollständigen FormData
        console.log('Vollständige Firmendaten im FormData:');
        for (const pair of completeFormData.entries()) {
          console.log(pair[0], pair[1] instanceof File ? `File: ${pair[1].name} (${pair[1].size} bytes)` : pair[1]);
        }
        
        // Sende die vollständigen Daten an den Backend-Endpunkt
        console.log('Sende PUT-Anfrage an /api/company mit vollständigen Firmendaten');
        const response = await http.put('/api/company', completeFormData);
        
        // Aktualisiere die Firma im Store
        if (response.data) {
          this.updateCompany({ id, data: response.data });
          return response.data;
        } else {
          console.warn('Keine Daten in der Antwort erhalten', response);
        }
        
        throw new Error('Keine Daten vom Server erhalten');
      } catch (error) {
        console.error('Fehler in updateCompanyLogo:', error);
        
        if (error.response) {
          console.log('Fehler-Antwort vom Server:', error.response.status, error.response.data);
          this.error = error.response.data.message || error.response.data.errors?.logo?.[0] || error.message;
        } else {
          this.error = error.message;
        }
        
        console.error('Fehler beim Aktualisieren des Firmenlogos:', error);
        throw error;
      } finally {
        this.loading = false;
      }
    },
    
    async updateCompanyColor(color) {
      if (!this.currentCompany) return;
      
      try {
        const response = await http.put(`/api/companies/${this.currentCompany.id}/settings`, {
          primary_color: color
        });
        
        this.updateCompany({ id: this.currentCompany.id, data: response.data });
        
        // CSS-Variable aktualisieren
        document.documentElement.style.setProperty('--primary-color', color);
        
        return response.data;
      } catch (error) {
        console.error('Fehler beim Aktualisieren der Firmenfarbe:', error);
        throw error;
      }
    },
    
    async fetchCompanies() {
      this.loading = true;
      this.error = null;
      
      try {
        const response = await http.get('/api/companies');
        this.companies = response.data;
        return this.companies;
      } catch (error) {
        console.error('Fehler beim Laden der Unternehmen:', error);
        this.error = error.response ? error.response.data.message : error.message;
        return [];
      } finally {
        this.loading = false;
      }
    },
    
    // Dashboard-Daten abrufen, mit Unterstützung für Impersonierung
    async fetchDashboardData() {
      this.dashboardLoading = true;
      this.dashboardError = null;
      
      try {
        // Sicherstellen, dass wir die neuesten Cookies haben
        // Dies ist besonders wichtig für Impersonierung
        await http.get('/sanctum/csrf-cookie');
        
        const authStore = useAuthStore();
        const isImpersonating = authStore.isImpersonating;
        const companyId = authStore.company?.id || authStore.user?.company_id;
        
        // Optionen vorbereiten für API-Aufruf
        const options = {};
        
        // Wenn wir eine Impersonierung haben, stellen wir sicher, dass die entsprechenden
        // Session-Informationen im Request gesendet werden
        if (isImpersonating && companyId) {
          options.headers = {
            'X-Impersonated-Company': companyId
          };
          console.log('Impersonierung aktiv: Sende X-Impersonated-Company Header:', companyId);
        }
        
        const response = await http.get('/api/company/dashboard', options);
        this.dashboardData = response.data;
        console.log('Dashboard-Daten erfolgreich abgerufen:', response.data);
        return response.data;
      } catch (error) {
        console.error('Fehler beim Abrufen der Dashboard-Daten:', error);
        this.dashboardError = error.response?.data?.message || 'Ein Fehler ist aufgetreten.';
        throw error;
      } finally {
        this.dashboardLoading = false;
      }
    },
    
    async updateSettings(formData) {
      this.loading = true
      this.error = null

      try {
        const response = await http.post(API_PATHS.COMPANY_SETTINGS, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        
        // Aktualisiere die Daten im Store
        this.companyData = response.data
        this.lastFetch = Date.now()
        return response.data
      } catch (error) {
        this.error = error.response?.data?.message || 'Fehler beim Aktualisieren der Unternehmensdaten'
        throw error
      } finally {
        this.loading = false
      }
    },
    
    // Methode zum Entfernen des Logos eines Unternehmens
    async removeCompanyLogo(companyId) {
      this.loading = true
      this.error = null

      try {
        // Wir erstellen ein FormData-Objekt mit einem Feld, das anzeigt, dass das Logo entfernt werden soll
        const formData = new FormData()
        formData.append('remove_logo', 'true')
        
        const response = await http.post(API_PATHS.COMPANY_SETTINGS, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        
        // Aktualisiere die Daten im Store
        this.companyData = response.data
        
        // Wenn das aktuelle Unternehmen aktualisiert wurde, setzen wir es auch als aktuelles
        if (this.currentCompany && this.currentCompany.id === companyId) {
          this.setCurrentCompany(response.data)
        }
        
        // Speichere die aktualisierte Firma im companies-Array
        this.addCompany(response.data)
        
        this.lastFetch = Date.now()
        return response.data
      } catch (error) {
        this.error = error.response?.data?.message || 'Fehler beim Entfernen des Logos'
        throw error
      } finally {
        this.loading = false
      }
    },
    
    setCompany(company) {
      // Delegiere zu addCompany und setCurrentCompany
      this.addCompany(company);
      this.setCurrentCompany(company);
    },

    async fetchCompanyData() {
      // Wenn Daten im Cache sind und nicht abgelaufen, verwende diese
      if (this.companyData && !this.isDataStale) {
        return this.companyData
      }

      this.isLoading = true
      this.error = null

      try {
        const response = await http.get('/api/company')
        this.companyData = response.data
        this.lastFetch = Date.now()
        return response.data
      } catch (error) {
        this.error = error.response?.data?.message || 'Fehler beim Laden der Unternehmensdaten'
        throw error
      } finally {
        this.isLoading = false
      }
    },

    // Neue Methode zum erzwungenen Neuladen der Daten
    async forceRefresh() {
      this.lastFetch = null
      return this.fetchCompanyData()
    },

    // Neue Methode zum Invalidate des Caches
    invalidateCache() {
      this.lastFetch = null
    }
  },
  
  persist: {
    paths: ['currentCompanyId', 'currentCompany']
  }
}); 